// GroupCard Component - Preview card for a group

import React from 'react';
import { Users, Lock, Globe } from 'lucide-react';
import type { Group, GroupCardData, GROUP_CATEGORIES } from '../../types/groups';

interface GroupCardProps {
  group: Group | GroupCardData;
  onClick?: () => void;
  showMemberCount?: boolean;
}

// Get category info
const getCategoryInfo = (category: string) => {
  const categories: Record<string, { label: string; color: string }> = {
    social: { label: 'Gesellig', color: 'bg-pink-100 text-pink-700' },
    hobby: { label: 'Hobby', color: 'bg-purple-100 text-purple-700' },
    neighborhood: { label: 'Nachbarschaft', color: 'bg-green-100 text-green-700' },
    food_drink: { label: 'Essen & Trinken', color: 'bg-orange-100 text-orange-700' },
    culture: { label: 'Kultur', color: 'bg-blue-100 text-blue-700' },
    learning: { label: 'Lernen', color: 'bg-yellow-100 text-yellow-700' },
    sports: { label: 'Sport', color: 'bg-red-100 text-red-700' },
    nature: { label: 'Natur', color: 'bg-emerald-100 text-emerald-700' },
    other: { label: 'Sonstiges', color: 'bg-gray-100 text-gray-700' },
  };
  return categories[category] || categories.other;
};

export function GroupCard({ group, onClick, showMemberCount = true }: GroupCardProps) {
  const categoryInfo = getCategoryInfo(group.category);
  const description = 'shortDescription' in group ? group.shortDescription : ('description' in group ? group.description?.slice(0, 100) : '');

  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl border border-gray-200 overflow-hidden transition-all ${
        onClick ? 'cursor-pointer hover:shadow-md hover:border-gray-300' : ''
      }`}
    >
      {/* Cover Image */}
      <div className="relative h-32 bg-gradient-to-br from-indigo-400 to-purple-500">
        {group.coverImageUrl ? (
          <img
            src={group.coverImageUrl}
            alt={group.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Users className="w-12 h-12 text-white/50" />
          </div>
        )}

        {/* Visibility Badge */}
        <div className="absolute top-2 right-2">
          {group.visibility === 'private' ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-black/50 text-white text-xs rounded-full">
              <Lock className="w-3 h-3" />
              Privat
            </span>
          ) : group.visibility === 'public' ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-black/50 text-white text-xs rounded-full">
              <Globe className="w-3 h-3" />
              Offen
            </span>
          ) : null}
        </div>

        {/* Avatar overlay */}
        {group.avatarUrl && (
          <div className="absolute -bottom-6 left-4">
            <img
              src={group.avatarUrl}
              alt=""
              className="w-12 h-12 rounded-full border-2 border-white object-cover"
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className={`p-4 ${group.avatarUrl ? 'pt-8' : ''}`}>
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-gray-900 line-clamp-1">{group.name}</h3>
          <span className={`flex-shrink-0 px-2 py-0.5 text-xs rounded-full ${categoryInfo.color}`}>
            {categoryInfo.label}
          </span>
        </div>

        {description && (
          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{description}</p>
        )}

        {showMemberCount && (
          <div className="flex items-center gap-1 mt-3 text-sm text-gray-500">
            <Users className="w-4 h-4" />
            <span>{group.memberCount} Mitglieder</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default GroupCard;
